import { createSelector } from 'reselect';
import cartUtils from '../utils/cart';
import { selectRetouchingAddableForOrder } from './retouchingGate';
import type { RetouchingRootState } from './retouchingState';

export interface RetouchingPreCartOfferEntry {
  orderKey: string;
  newPoseIds: number[];
}

const EMPTY_OFFERS: RetouchingPreCartOfferEntry[] = [];

interface PreCartCartItem {
  isRetouching?: boolean;
  poseIds?: (number | string)[];
}

interface PreCartOrder {
  cartItems?: PreCartCartItem[];
  retouching?: { offeredPoseIds?: (number | string)[] };
}

const selectOrders = (
  state: RetouchingRootState,
): Record<string, PreCartOrder> => state.v3Order?.orders || {};

// The keys of the orders retouching can be added to, as one string so the
// result below is recomputed only when the gate's answer changes.
const selectAddableOrderKeys = (state: RetouchingRootState): string =>
  JSON.stringify(
    Object.keys(selectOrders(state)).filter((orderKey) =>
      selectRetouchingAddableForOrder(state, orderKey),
    ),
  );

// The photos on an order that were never on a retouching screen and are not
// retouched yet. Group photos are never selectable, so never new. A cart
// persisted before the offered set existed reads it as empty.
const newPoseIdsForOrder = (order: PreCartOrder | undefined): number[] => {
  const offered = new Set(
    (order?.retouching?.offeredPoseIds || []).map(Number),
  );
  const retouched = new Set(
    (order?.cartItems || [])
      .filter((item) => item?.isRetouching)
      .flatMap((item) => (item.poseIds || []).map(Number)),
  );
  return cartUtils
    .distinctCartPoses(order)
    .map((pose: { id: number | string }) => Number(pose.id))
    .filter((id: number) => !offered.has(id) && !retouched.has(id));
};

// One entry per order, in cart order, that should see the pre-cart retouching
// offer before the cart renders.
export const selectRetouchingPreCartOffers = createSelector(
  selectOrders,
  selectAddableOrderKeys,
  (orders, addableKeysJson): RetouchingPreCartOfferEntry[] => {
    const offers = (JSON.parse(addableKeysJson) as string[])
      .map((orderKey) => ({
        orderKey,
        newPoseIds: newPoseIdsForOrder(orders[orderKey]),
      }))
      .filter((offer) => offer.newPoseIds.length > 0);
    return offers.length ? offers : EMPTY_OFFERS;
  },
);
